import { storableError } from '../../util/errors';
import { skillFitCompetencyFeedback } from '../../util/api';

/**
 * `SkillFitCandidateFeedbackPage` state — the developmental feedback a CANDIDATE reads at
 * `/skillfit-feedback/:skillFitCheckId`.
 *
 * ⛔ **This is not the report page's state under another name.** The reviewer's page fetches
 * `GET /api/skillFitReport`, which 403s non-customers by design; this one fetches
 * `GET /api/skillFitCompetencyFeedback`, a separate candidate-scoped endpoint whose response is a
 * field-named projection — area, score, its wording, the summary sentence, and nothing else.
 *
 * ⚠ `skillFitCheckId` is the row's join key, ⛔ NOT a credential. It arrives in the URL because the
 * candidate's own check list built the link from its own row; the server resolves the candidate from
 * the session and searches only that candidate's invitations, so pasting someone else's id resolves
 * nothing.
 */

// ================ Action types ================ //

export const FETCH_FEEDBACK_REQUEST = 'app/SkillFitCandidateFeedbackPage/FETCH_FEEDBACK_REQUEST';
export const FETCH_FEEDBACK_SUCCESS = 'app/SkillFitCandidateFeedbackPage/FETCH_FEEDBACK_SUCCESS';
export const FETCH_FEEDBACK_ERROR = 'app/SkillFitCandidateFeedbackPage/FETCH_FEEDBACK_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  feedback: null,
};

export default function skillFitCandidateFeedbackPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_FEEDBACK_REQUEST:
      // ⛔ The previous feedback is dropped on the way IN. A candidate can hold several checks, all
      // reached through this one page — leaving the old rows standing while the next request is in
      // flight would show one assessment's feedback under another assessment's address.
      return { ...state, fetchInProgress: true, fetchError: null, feedback: null };
    case FETCH_FEEDBACK_SUCCESS:
      return { ...state, fetchInProgress: false, feedback: payload };
    case FETCH_FEEDBACK_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchFeedbackRequest = () => ({ type: FETCH_FEEDBACK_REQUEST });
export const fetchFeedbackSuccess = payload => ({ type: FETCH_FEEDBACK_SUCCESS, payload });
export const fetchFeedbackError = e => ({ type: FETCH_FEEDBACK_ERROR, error: true, payload: e });

/**
 * ⛔ **Resolves even when the fetch fails, and never rethrows.** The endpoint's refusals are this
 * page's content, not a broken load:
 *  - `403` — the employer has not shared this feedback (or has withdrawn it since).
 *  - `404` — the check is not this candidate's, or its assessment is not available.
 * The page shows the SAME state for both (see `resolveViewState`), so a candidate can never tell a
 * withheld assessment from one that does not exist. Rejecting here would fail the data-loading pass
 * and show a blank page instead of the server's answer.
 */
export const loadData = params => dispatch => {
  const skillFitCheckId = params && params.skillFitCheckId;

  dispatch(fetchFeedbackRequest());
  return skillFitCompetencyFeedback(skillFitCheckId)
    .then(response => {
      dispatch(fetchFeedbackSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchFeedbackError(storableError(e)));
    });
};
